import { createContext, useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'
import { usePageTitle } from '@calendly/hooks'
import { useNewCode } from '@calendly/feature-check-react'
import detect from 'detect.js'

import { useI18n } from 'locales'
import { useEventType } from 'components/use_event_type'
import { emailSelector, nameSelector, uuidSelector, emailAsUsernameSelector } from 'selectors/invitee'
import { eventSelector } from 'selectors'
import { currentUserSelector } from 'selectors/user'
import { useInviteeSignUp } from 'hooks/useInviteeSignUp'
import { anonymousIdSelector } from 'selectors/segment'
import { toFirstLast } from 'utils/strings'

const ConfirmationPageDataContext = createContext(null)

const useLinks = () => {
  const eventType = useEventType()
  const user = useSelector(currentUserSelector)

  const {
    show_custom_links: showCustomLinks,
    custom_links: customLinks,
    another_event_button_enabled: anotherButtonEnabled,
    confirmation_page_type: confirmationPageType,
  } = eventType

  const hideLinks = (!anotherButtonEnabled && !customLinks?.length) || confirmationPageType === 'external'

  const showDetails = !hideLinks || !user

  return {
    hideLinks,
    showDetails,
    showCustomLinks,
    anotherButtonEnabled,
  }
}

/**
 * @typedef {Object} ConfirmationPageData
 * @property {*}                  event                 - The Calendly Event Instance
 * @property {*}                  eventType             - The Calendly Event Type
 * @property {boolean}            isMeetingPollFlow     - `true` when this is a poll flow
 * @property {boolean}            isReschedulingFlow    - `true` when in a reschedule flow
 * @property {string}             title                 - Page title text
 * @property {string}             subtitle              - Subtitle text
 * @property {string|undefined}   confirmationName      - `event.publishers_list`
 * @property {boolean}            shouldSeeGmailSniperLink
 * @property {boolean}            useNewConfirmation    - feature-flag guard
 * @property {string|null}        firstName             - lowercased invitee first name
 * @property {string|null}        fullName              - raw invitee full name
 * @property {string|null}        lastName              - lowercased invitee last name
 * @property {*|null}             currentUser           - Redux current user
 * @property {string}             anonymousId           - Segment anonymous ID
 * @property {boolean}            showDetails           - `true` when there are links to show or the user is not logged in
 * @property {string}             signUpByEmailPath     - Path for invitee sign up
 * @property {string}             inviteeEmail          - Email address of the invitee
 * @property {string}             inviteeUuid           - UUID of the invitee
 */

/**
 * Hook to read all confirmation‐page values.
 * @returns {ConfirmationPageData}
 */
export function ConfirmationPageDataWrapper({ children }) {
  const inviteeEmail = useSelector(emailSelector)
  const inviteeEmailAsUsername = useSelector(emailAsUsernameSelector)
  const inviteeUuid = useSelector(uuidSelector)
  const event = useSelector(eventSelector)
  const user = useSelector(currentUserSelector)
  const anonymousId = useSelector(anonymousIdSelector)
  const fullName = useSelector(nameSelector)
  const { path } = useRouteMatch()
  const { i18n } = useI18n()
  const eventType = useEventType()
  const { showDetails } = useLinks()

  const useNewConfirmation = useNewCode('sb_booking_confirmation_sso_feature', false)?.enabled
  const sniperLinkFeatureActive = useNewCode('sb_sniper_links_feature', false)?.enabled

  const isReschedulingFlow = path === '/reschedulings/:inviteeUuid/confirmation'
  const isMeetingPollFlow = path === '/d/:linkUid/:eventTypeName/confirmation'

  let title
  if (isMeetingPollFlow) {
    title = i18n.t('booking.templates.poll.confirmation.title')
  } else if (isReschedulingFlow) {
    title = i18n.t('booking.templates.reschedule_confirmed_title')
  } else {
    title = i18n.t('booking.templates.confirmed_title')
  }

  usePageTitle(`${title} - Calendly`)

  const confirmationName = event?.publishers_list

  let subtitle
  if (isReschedulingFlow) {
    subtitle = i18n.t('booking.templates.rescheduled_with', { name: confirmationName })
  } else {
    subtitle = i18n.t('booking.templates.scheduled_with', { name: confirmationName })
  }
  const ua = detect.parse(navigator.userAgent)
  const notIphone = ua.device.name !== 'iPhone'
  const shouldSeeGmailSniperLink = inviteeEmail && inviteeEmail.includes('@gmail.com') && sniperLinkFeatureActive && notIphone

  const { first: firstName, last: lastName } = toFirstLast(fullName)
  const { signUpByEmailPath } = useInviteeSignUp()
  const showFooter = !user && !eventType?.unbranded

  const checkIfIframe = () => window.location !== window.parent.location
  const isEmbedded = checkIfIframe()
  const unbranded = eventType?.unbranded

  const value = useMemo(() => ({
    event,
    isMeetingPollFlow,
    isReschedulingFlow,
    title,
    subtitle,
    confirmationName,
    shouldSeeGmailSniperLink,
    useNewConfirmation,
    eventType,
    firstName,
    fullName,
    lastName,
    currentUser: user,
    anonymousId,
    showDetails,
    signUpByEmailPath,
    inviteeEmail,
    inviteeEmailAsUsername,
    inviteeUuid,
    showFooter,
    isEmbedded,
    unbranded
  }), [
    event,
    isMeetingPollFlow,
    isReschedulingFlow,
    title,
    subtitle,
    confirmationName,
    shouldSeeGmailSniperLink,
    useNewConfirmation,
    eventType,
    firstName,
    fullName,
    lastName,
    user,
    anonymousId,
    showDetails,
    signUpByEmailPath,
    inviteeEmail,
    inviteeEmailAsUsername,
    inviteeUuid,
    showFooter,
    isEmbedded,
    unbranded
  ])

  return (
    <ConfirmationPageDataContext.Provider value={value}>
      {children}
    </ConfirmationPageDataContext.Provider>
  )
}

export function useConfirmationPageData() {
  const context = useContext(ConfirmationPageDataContext)
  if (!context) {
    throw new Error('useConfirmationPageData must be used within a ConfirmationPageDataProvider')
  }
  return context
}
